import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ShoppingBag, Menu, X, User, ChevronDown, Heart } from "lucide-react";
import { cartUtils, wishlistUtils } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Categories with their subcategories — matches backend /api/subcategories
const CATEGORIES = [
  {
    slug: "sofa",
    name: "Sofa",
    subs: ["Sofa", "Sofa cum Bed", "Center Table", "Corners", "Lounge Chair"],
  },
  {
    slug: "bedroom",
    name: "Bedroom",
    subs: ["Bed", "Bed Side Table", "Wardrobe", "Dressing Table", "TV Cabinet", "Sofa cum Bed", "Side Table", "Wall Shelf"],
  },
  {
    slug: "living-room",
    name: "Living Room",
    subs: ["Coffee Table", "TV Cabinet", "Wall Shelf", "Book Shelf", "Sideboard", "Sofa", "Bench", "Shoe Rack Cabinet", "Lounge Chair", "Cabinet", "Temple", "Corners", "Crockery", "Kitchen Cabinet"],
  },
  {
    slug: "dining",
    name: "Dining",
    subs: ["6 Seater Dining Table", "4 Seater Dining Table", "Dining Table", "Chair", "Bench"],
  },
  {
    slug: "storage",
    name: "Storage",
    subs: ["Sideboard", "Cabinet", "Wardrobe", "Crockery", "Wall Shelf", "Book Shelf", "Temple", "Chester"],
  },
  {
    slug: "study-office",
    name: "Study & Office",
    subs: ["Study Table", "Computer Table", "Chair", "Book Shelf", "Office Table", "Table", "Temple", "Wall Shelf"],
  },
  {
    slug: "bar",
    name: "Bar Furniture",
    subs: ["Bar Cabinet", "Bar Chair", "Bar Trolly", "Wall Shelf", "Corners"],
  },
];

const SECONDARY_LINKS = [
  { to: "/custom-order", label: "Custom Order" },
  { to: "/track-order", label: "Track Order" },
  { to: "/about", label: "About" },
];

// Turn subcategory into the URL query param key backend understands
const subLink = (catSlug, sub) => `/shop/${catSlug}?sub=${encodeURIComponent(sub)}`;

export default function Navbar() {
  const [count, setCount] = useState(0);
  const [wlCount, setWlCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null); // slug of expanded mobile category
  const [openCat, setOpenCat] = useState(null); // slug of the currently open desktop dropdown
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const rowRef = useRef(null);

  useEffect(() => {
    const update = () => setCount(cartUtils.count());
    const updateWl = () => setWlCount(wishlistUtils.count());
    update();
    updateWl();
    window.addEventListener("cart-updated", update);
    window.addEventListener("storage", update);
    window.addEventListener("wishlist-updated", updateWl);
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    const onClick = (e) => { if (rowRef.current && !rowRef.current.contains(e.target)) setOpenCat(null); };
    document.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("cart-updated", update);
      window.removeEventListener("storage", update);
      window.removeEventListener("wishlist-updated", updateWl);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const closeMobile = () => { setMobileOpen(false); setMobileExpanded(null); };

  return (
    <header data-testid="site-navbar" className={`sticky top-0 z-40 backdrop-blur-xl transition-all ${scrolled ? "bg-[#FAF8F5]/95 border-b border-stone-200" : "bg-[#FAF8F5]/80"}`}>
      {/* Top utility strip — Custom Order / Track Order / About */}
      <div className="hidden md:block border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-2 flex items-center justify-between text-[11px] tracking-widest uppercase text-stone-500">
          <span>Handcrafted in Bathinda · Since a lineage of woodworkers</span>
          <div className="flex items-center gap-6">
            {SECONDARY_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-utility-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="hover:text-stone-900"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Main row — logo + 7 categories + login/cart */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <img src="/vf-logo.jpg" alt="Vardhman Furniture" className="h-11 w-11 md:h-12 md:w-12 object-cover rounded-full ring-1 ring-stone-300 shadow-sm"/>
          <span className="flex flex-col leading-none">
            <span className="font-serif text-2xl md:text-[26px] tracking-tight text-stone-900">Vardhman</span>
            <span className="hidden sm:inline text-[10px] tracking-[0.3em] uppercase text-[#8B5A2B] mt-1">Furniture</span>
          </span>
        </Link>

        {/* Desktop categories */}
        <nav
          ref={rowRef}
          className="hidden lg:flex items-center gap-1 xl:gap-2"
          onMouseLeave={() => setOpenCat(null)}
        >
          {CATEGORIES.map((c) => {
            const active = openCat === c.slug;
            return (
              <div
                key={c.slug}
                className="relative"
                onMouseEnter={() => setOpenCat(c.slug)}
              >
                <Link
                  to={`/shop/${c.slug}`}
                  data-testid={`nav-cat-${c.slug}`}
                  onClick={() => setOpenCat(null)}
                  className={`flex items-center gap-1 px-2.5 py-2 text-[13px] tracking-wide whitespace-nowrap transition-colors ${active ? "text-stone-900" : "text-stone-700 hover:text-stone-900"}`}
                >
                  {c.name}
                  <ChevronDown size={12} strokeWidth={1.6} className={`transition-transform ${active ? "rotate-180" : ""}`}/>
                </Link>
                {active && (
                  <div
                    data-testid={`nav-cat-dropdown-${c.slug}`}
                    className="absolute left-0 top-full pt-2 w-64 z-50"
                  >
                    <div className="bg-white border border-stone-200 shadow-xl py-2">
                      <Link
                        to={`/shop/${c.slug}`}
                        onClick={() => setOpenCat(null)}
                        data-testid={`nav-cat-${c.slug}-all`}
                        className="block px-4 py-2 text-[11px] tracking-[0.24em] uppercase text-[#8B5A2B] hover:bg-[#FAF6EF]"
                      >
                        All {c.name}
                      </Link>
                      <div className="border-t border-stone-100 my-1"/>
                      {c.subs.map((s) => (
                        <Link
                          key={s}
                          to={subLink(c.slug, s)}
                          onClick={() => setOpenCat(null)}
                          data-testid={`nav-sub-${c.slug}-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="block px-4 py-1.5 text-[13px] text-stone-700 hover:bg-[#FAF6EF] hover:text-stone-900"
                        >
                          {s}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-4 shrink-0">
          {user ? (
            <div className="hidden md:flex items-center gap-3">
              {user.role === "admin" && <Link to="/admin" data-testid="nav-admin-link" className="text-xs tracking-widest uppercase text-[#8B5A2B]">Admin</Link>}
              <Link to="/account" data-testid="nav-account-link" className="flex items-center gap-1 text-sm"><User size={16}/> {user.name.split(" ")[0]}</Link>
              <button data-testid="nav-logout-btn" onClick={() => { logout(); nav("/"); }} className="text-xs tracking-widest uppercase text-stone-500 hover:text-stone-900">Logout</button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login-link" className="hidden md:inline text-xs tracking-widest uppercase text-stone-700 hover:text-stone-900">Login</Link>
          )}
          <Link to="/wishlist" data-testid="nav-wishlist-link" aria-label="Wishlist" className="relative flex items-center gap-1">
            <Heart size={20} strokeWidth={1.5} />
            {wlCount > 0 && <span data-testid="nav-wishlist-count" className="absolute -top-2 -right-3 bg-[#B23A3A] text-white text-[10px] w-5 h-5 flex items-center justify-center">{wlCount}</span>}
          </Link>
          <Link to="/cart" data-testid="nav-cart-link" className="relative flex items-center gap-1">
            <ShoppingBag size={20} strokeWidth={1.5} />
            {count > 0 && <span data-testid="nav-cart-count" className="absolute -top-2 -right-3 bg-[#1C1917] text-white text-[10px] w-5 h-5 flex items-center justify-center">{count}</span>}
          </Link>
          <button data-testid="nav-menu-toggle" className="lg:hidden" onClick={() => setMobileOpen((v) => !v)}>
            {mobileOpen ? <X size={22}/> : <Menu size={22}/>}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-stone-200 bg-[#FAF8F5] max-h-[75vh] overflow-y-auto">
          <div className="px-6 py-4 flex flex-col gap-1">
            {CATEGORIES.map((c) => (
              <div key={c.slug} className="border-b border-stone-200 last:border-b-0">
                <button
                  data-testid={`mnav-cat-${c.slug}`}
                  onClick={() => setMobileExpanded(mobileExpanded === c.slug ? null : c.slug)}
                  className="w-full flex items-center justify-between py-3 text-left text-stone-900 font-medium"
                >
                  {c.name}
                  <ChevronDown size={14} strokeWidth={1.6} className={`transition-transform ${mobileExpanded === c.slug ? "rotate-180" : ""}`}/>
                </button>
                {mobileExpanded === c.slug && (
                  <div className="pb-3 pl-3 flex flex-col gap-1">
                    <Link to={`/shop/${c.slug}`} onClick={closeMobile} data-testid={`mnav-cat-${c.slug}-all`} className="py-1.5 text-[11px] tracking-widest uppercase text-[#8B5A2B]">All {c.name}</Link>
                    {c.subs.map((s) => (
                      <Link
                        key={s}
                        to={subLink(c.slug, s)}
                        onClick={closeMobile}
                        data-testid={`mnav-sub-${c.slug}-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="py-1.5 text-sm text-stone-700"
                      >
                        {s}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="mt-3 pt-3 border-t border-stone-200 flex flex-col gap-3">
              <Link to="/wishlist" onClick={closeMobile} data-testid="mnav-wishlist" className="text-stone-800 flex items-center gap-2"><Heart size={14}/> Wishlist{wlCount > 0 && <span className="ml-1 text-[10px] bg-[#B23A3A] text-white px-1.5 py-0.5">{wlCount}</span>}</Link>
              {SECONDARY_LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={closeMobile} data-testid={`mnav-${l.label.toLowerCase().replace(/\s/g, "-")}`} className="text-stone-800">{l.label}</Link>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-stone-200">
              {user ? (
                <>
                  {user.role === "admin" && <Link to="/admin" onClick={closeMobile} className="block text-[#8B5A2B] uppercase text-xs tracking-widest mb-2">Admin Panel</Link>}
                  <Link to="/account" onClick={closeMobile} className="block mb-2">My Account</Link>
                  <button onClick={() => { logout(); closeMobile(); nav("/"); }} className="text-left text-stone-500">Logout</button>
                </>
              ) : <Link to="/login" onClick={closeMobile}>Login / Sign up</Link>}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
